import qs from 'qs';
import { axiosApiInstance } from 'Store';
import { FlipFilter, FlipFilterRequest, SaleFlatDtoParamCalculation } from 'utils/types/FlipModels';
import { v4 } from 'uuid';

import { activeNotification } from 'components/functions/activeNotification';

import { userApiRequests } from './user';

const getFlipDefaultValues = async (): Promise<FlipFilterRequest> => {
    return axiosApiInstance
        .get('/flip')
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getFlipDefaultValues' });
            activeNotification(
                'Nie udało się pobrać danych',
                'Nie udało się pobrać danych szczegółowych rezerwacji',
                'warning'
            );

            return {};
        });
};

const getFlipIndividualOfferValues = async (id:number): Promise<FlipFilterRequest> => {
    return axiosApiInstance
        .get(`/flip?sale=${id}`)
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'getFlipIndividualOfferValues' });
            activeNotification(
                'Nie udało się pobrać danych',
                'Nie udało się pobrać danych szczegółowych rezerwacji',
                'warning'
            );

            return {};
        });
};

const deleteFlipIndividualOfferValues = async (id:number): Promise<FlipFilterRequest> => {
    return axiosApiInstance
        .delete(`/flip?sale=${id}`)
        .then((resp) => {
            if (resp.data) {
                activeNotification('Udało się!', 'Wartości zostały zresetowane na domyślne', 'success');

                return resp.data;
            }
        })
        .catch((err) => {
            const errorId = v4();
            const status = err?.response?.status;

            // 404 oznacza że wartości kalkulatora zostały już wcześniej zresetowane i obecnie są flltry domyślne
            if (status === 404) return;

            userApiRequests.logException(err, errorId, { functionName: 'deleteFlipIndividualOfferValues' });
            activeNotification(
                'Nie udało się zresetować wartości',
                'Zresestowanie wartości kalkulatora nie powiodło się, spróbuj ponownie później',
                'warning'
            );

            return {};
        });
};

const resetDefaultFlipValues = async (params: FlipFilterRequest): Promise<FlipFilter> => {
    return axiosApiInstance({
        method: 'put',
        url: '/flip',
        data: params,
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Wartości zostały zresetowane', 'success');

            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'resetDefaultFlipValues' });
            activeNotification(
                'Aktualizacja nie powiodła się',
                'Nie udało się zaktualizować wartości kalkulatora',
                'warning'
            );

            return {};
        });
};

const updateDefaultFlipValues = async (params: FlipFilterRequest): Promise<FlipFilter> => {
    return axiosApiInstance({
        method: 'put',
        url: '/flip',
        data: params,
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Wartości zostały zaktualizowane', 'success');

            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'updateDefaultFlipValues' });
            activeNotification(
                'Aktualizacja nie powiodła się',
                'Nie udało się zaktualizować wartości kalkulatora',
                'warning'
            );

            return {};
        });
};

const updateFlipIndividualOfferValues = async (params: FlipFilterRequest, id: number): Promise<FlipFilter> => {
    return axiosApiInstance({
        method: 'put',
        url: `/flip?sale=${id}`,
        data: params,
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Wartości zostały zaktualizowane', 'success');

            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'updateFlipIndividualOfferValues' });
            activeNotification(
                'Aktualizacja nie powiodła się',
                'Nie udało się aktualizować wartości kalkulatora',
                'warning'
            );

            return {};
        });
};

const updateFlipTotalIndividualOfferValues = async (data: SaleFlatDtoParamCalculation, param: string): Promise<number> => {
    return axiosApiInstance({
        method: 'post',
        url: `/flip/calculate/${param}`,
        data: data,
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'updateFlipTotalIndividualOfferValues' });

            return 0;
        });
};

const createFlipIndividualOfferValues = async (params: FlipFilterRequest): Promise<FlipFilter> => {
    return axiosApiInstance({
        method: 'put',
        url: '/flip',
        data: params,
        paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
        }
    })
        .then((resp) => {
            activeNotification('Udało się!', 'Wartości zostały zaktualizowane', 'success');

            return resp.data;
        })
        .catch((err) => {
            const errorId = v4();
            userApiRequests.logException(err, errorId, { functionName: 'createFlipIndividualOfferValues' });
            activeNotification(
                'Aktualizacja nie powiodła się',
                'Nie udało się aktualizować wartości kalkulatora',
                'warning'
            );

            return {};
        });
};

export const flipApiRequests = {
    getFlipDefaultValues,
    resetDefaultFlipValues,
    updateDefaultFlipValues,
    getFlipIndividualOfferValues,
    deleteFlipIndividualOfferValues,
    updateFlipIndividualOfferValues,
    updateFlipTotalIndividualOfferValues,
    createFlipIndividualOfferValues
};
